import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "form-container" }
const _hoisted_2 = { class: "selection-card" }
const _hoisted_3 = { class: "selection-types" }
const _hoisted_4 = {
  key: 0,
  class: "search-section"
}
const _hoisted_5 = { class: "search-tabs" }
const _hoisted_6 = { class: "search-input" }
const _hoisted_7 = {
  key: 0,
  class: "search-results"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 0,
  class: "selected-user"
}
const _hoisted_10 = { class: "message-content" }
const _hoisted_11 = { class: "image-upload-container" }
const _hoisted_12 = {
  key: 0,
  class: "upload-area"
}
const _hoisted_13 = { class: "upload-trigger" }
const _hoisted_14 = {
  key: 1,
  class: "image-preview-container"
}
const _hoisted_15 = ["src"]
const _hoisted_16 = { class: "image-overlay" }

import { ref, onMounted, reactive, computed, watch } from 'vue'
import {
  getUsersStats,
  searchUsers,
  pushOneNotification,
  pushManyNotification,
} from '@/api/user'
import { ElMessage } from 'element-plus'
import {
  Search,
  UploadFilled,
  Promotion,
  Delete,
} from '@element-plus/icons-vue'

interface UploadFile {
  raw: File
}

interface User {
  id: string
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  status: string
  created_at: string
}

interface UserStats {
  active: number
  inactive: number
  incomplete: number
  verifying: number
  total: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Notifications',
  setup(__props) {

const form = reactive({
  messageTextAz: '',
  messageTextEn: '',
  messageTextRu: '',
  titleAz: '',
  titleEn: '',
  titleRu: '',
  image: null as File | null,
})

const stats = ref<UserStats>({
  active: 0,
  inactive: 0,
  incomplete: 0,
  verifying: 0,
  total: 0,
})

const selectionType = ref<'all' | 'active' | 'inactive' | 'search'>('all')
const searchType = ref<'name' | 'id' | 'phone'>('name')
const searchQuery = ref('')
const searchResults = ref<User[]>([])
const selectedUser = ref<User | null>(null)
const selectedImage = ref<File | null>(null)
const imagePreview = ref<string>('')
const isSending = ref(false)

// Computed
const canSendNotification = computed(() => {
  // Check if message content is valid
  const hasValidMessage =
    form.titleAz &&
    form.titleEn &&
    form.titleRu &&
    form.messageTextAz &&
    form.messageTextEn &&
    form.messageTextRu

  // Check if user selection is valid
  const hasValidSelection =
    selectionType.value === 'all' ||
    selectionType.value === 'active' ||
    selectionType.value === 'inactive' ||
    selectedUser.value !== null

  return hasValidMessage && hasValidSelection
})

// Methods
const fetchStats = async () => {
  try {
    const response = await getUsersStats()
    stats.value = response.data
  } catch (error) {
    console.error('Failed to fetch user stats:', error)
    ElMessage.error('Failed to load user statistics')
  }
}

let searchTimeout: ReturnType<typeof setTimeout> | null = null
const handleSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  if (!searchQuery.value.trim()) {
    searchResults.value = []
    return
  }

  searchTimeout = setTimeout(async () => {
    try {
      const response = await searchUsers(searchQuery.value, searchType.value)
      searchResults.value = response.data
    } catch (error) {
      console.error('Search failed:', error)
      searchResults.value = []
    }
  }, 300)
}

const selectUser = (user: User) => {
  selectedUser.value = user
  searchResults.value = []
  searchQuery.value = ''
}

const clearSelection = () => {
  selectedUser.value = null
}

const getSearchPlaceholder = () => {
  switch (searchType.value) {
    case 'name':
      return 'Enter name or surname...'
    case 'id':
      return 'Enter user ID...'
    case 'phone':
      return 'Enter phone number...'
    default:
      return 'Search...'
  }
}

const handleImageUpload = (file: UploadFile) => {
  selectedImage.value = file.raw
  const reader = new FileReader()
  reader.onload = e => {
    imagePreview.value = e.target?.result as string
  }
  reader.readAsDataURL(file.raw)
}

const clearImage = () => {
  selectedImage.value = null
  imagePreview.value = ''
}

const sendNotification = async () => {
  try {
    isSending.value = true
    const formData = new FormData()

    // Add message content
    formData.append('titleAz', form.titleAz)
    formData.append('titleEn', form.titleEn)
    formData.append('titleRu', form.titleRu)
    formData.append('messageTextAz', form.messageTextAz)
    formData.append('messageTextEn', form.messageTextEn)
    formData.append('messageTextRu', form.messageTextRu)

    // Add image if present
    if (selectedImage.value) {
      formData.append('image', selectedImage.value)
    }

    // Add user selection data
    let response
    switch (selectionType.value) {
      case 'all':
        formData.append('allUsers', 'true')
        response = await pushManyNotification(formData)
        break
      case 'active':
        formData.append('allActive', 'true')
        response = await pushManyNotification(formData)
        break
      case 'inactive':
        formData.append('allInactive', 'true')
        response = await pushManyNotification(formData)
        break
      case 'search':
        if (selectedUser.value) {
          formData.append('userId', selectedUser.value.id)
          formData.append('phoneNumber', selectedUser.value.phone || '')
          response = await pushOneNotification(formData)
        } else {
          throw new Error('Please select a user')
        }
        break
      default:
        throw new Error('Invalid selection type')
    }

    ElMessage.success(
      response.data.message || 'Notification sent successfully!'
    )
    resetForm()
  } catch (error) {
    console.error('Send notification failed:', error)
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to send notification'
    ElMessage.error(errorMessage)
  } finally {
    isSending.value = false
  }
}

const resetForm = () => {
  Object.assign(form, {
    titleAz: '',
    titleEn: '',
    titleRu: '',
    messageTextAz: '',
    messageTextEn: '',
    messageTextRu: '',
  })
  selectedUser.value = null
  selectedImage.value = null
  imagePreview.value = ''
  selectionType.value = 'all'
}

// Watchers
watch(selectionType, newValue => {
  if (newValue !== 'search') {
    selectedUser.value = null
    searchResults.value = []
    searchQuery.value = ''
  }
})

watch(searchType, () => {
  searchResults.value = []
  searchQuery.value = ''
})

// Lifecycle
onMounted(() => {
  fetchStats()
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_alert = _resolveComponent("el-alert")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_divider = _resolveComponent("el-divider")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_upload = _resolveComponent("el-upload")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_scrollbar = _resolveComponent("el-scrollbar")!

  return (_openBlock(), _createBlock(_component_el_scrollbar, { always: "" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_el_form, {
          model: form,
          "label-width": "120px",
          class: "custom-form"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 24 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_2, [
                      _cache[21] || (_cache[21] = _createElementVNode("p", { class: "section-title" }, "Send notification to", -1)),
                      _createElementVNode("div", _hoisted_3, [
                        _createVNode(_component_el_button, {
                          type: selectionType.value === 'all' ? 'primary' : 'default',
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (selectionType.value = 'all')),
                          class: "toggle-button"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" All Users (" + _toDisplayString(stats.value.total) + ") ", 1)
                          ]),
                          _: 1
                        }, 8, ["type"]),
                        _createVNode(_component_el_button, {
                          type: selectionType.value === 'active' ? 'primary' : 'default',
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (selectionType.value = 'active')),
                          class: "toggle-button"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" All Active (" + _toDisplayString(stats.value.active) + ") ", 1)
                          ]),
                          _: 1
                        }, 8, ["type"]),
                        _createVNode(_component_el_button, {
                          type: selectionType.value === 'inactive' ? 'primary' : 'default',
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (selectionType.value = 'inactive')),
                          class: "toggle-button"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" All Inactive (" + _toDisplayString(stats.value.inactive) + ") ", 1)
                          ]),
                          _: 1
                        }, 8, ["type"]),
                        _createVNode(_component_el_button, {
                          type: selectionType.value === 'search' ? 'primary' : 'default',
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (selectionType.value = 'search')),
                          class: "toggle-button"
                        }, {
                          default: _withCtx(() => _cache[14] || (_cache[14] = [
                            _createTextVNode(" Search User ")
                          ])),
                          _: 1,
                          __: [14]
                        }, 8, ["type"])
                      ]),
                      (selectionType.value === 'search')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createElementVNode("div", _hoisted_5, [
                              _createVNode(_component_el_button, {
                                type: searchType.value === 'name' ? 'primary' : 'default',
                                size: "small",
                                onClick: _cache[4] || (_cache[4] = ($event: any) => (searchType.value = 'name'))
                              }, {
                                default: _withCtx(() => _cache[15] || (_cache[15] = [
                                  _createTextVNode(" By Name ")
                                ])),
                                _: 1,
                                __: [15]
                              }, 8, ["type"]),
                              _createVNode(_component_el_button, {
                                type: searchType.value === 'id' ? 'primary' : 'default',
                                size: "small",
                                onClick: _cache[5] || (_cache[5] = ($event: any) => (searchType.value = 'id'))
                              }, {
                                default: _withCtx(() => _cache[16] || (_cache[16] = [
                                  _createTextVNode(" By ID ")
                                ])),
                                _: 1,
                                __: [16]
                              }, 8, ["type"]),
                              _createVNode(_component_el_button, {
                                type: searchType.value === 'phone' ? 'primary' : 'default',
                                size: "small",
                                onClick: _cache[6] || (_cache[6] = ($event: any) => (searchType.value = 'phone'))
                              }, {
                                default: _withCtx(() => _cache[17] || (_cache[17] = [
                                  _createTextVNode(" By Phone ")
                                ])),
                                _: 1,
                                __: [17]
                              }, 8, ["type"])
                            ]),
                            _createElementVNode("div", _hoisted_6, [
                              _createVNode(_component_el_input, {
                                modelValue: searchQuery.value,
                                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((searchQuery).value = $event)),
                                placeholder: getSearchPlaceholder(),
                                onInput: handleSearch,
                                clearable: ""
                              }, {
                                prefix: _withCtx(() => [
                                  _createVNode(_component_el_icon, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(Search))
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }, 8, ["modelValue", "placeholder"]),
                              (searchResults.value.length > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchResults.value, (user) => {
                                      return (_openBlock(), _createElementBlock("div", {
                                        key: user.id,
                                        onClick: ($event: any) => (selectUser(user)),
                                        class: "search-result-item"
                                      }, [
                                        _createTextVNode(_toDisplayString(user.first_name) + " " + _toDisplayString(user.last_name) + " ", 1),
                                        _createElementVNode("small", null, _toDisplayString(user.email), 1)
                                      ], 8, _hoisted_8))
                                    }), 128))
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            (selectedUser.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                  _createVNode(_component_el_alert, {
                                    title: 
                      `Selected: ${selectedUser.value.first_name} ${selectedUser.value.last_name}`
                    ,
                                    type: "success",
                                    closable: true,
                                    onClose: clearSelection
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", null, [
                                        _createElementVNode("p", null, [
                                          _cache[18] || (_cache[18] = _createElementVNode("strong", null, "ID:", -1)),
                                          _createTextVNode(" " + _toDisplayString(selectedUser.value.id), 1)
                                        ]),
                                        _createElementVNode("p", null, [
                                          _cache[19] || (_cache[19] = _createElementVNode("strong", null, "Phone:", -1)),
                                          _createTextVNode(" " + _toDisplayString(selectedUser.value.phone), 1)
                                        ]),
                                        _createElementVNode("p", null, [
                                          _cache[20] || (_cache[20] = _createElementVNode("strong", null, "Email:", -1)),
                                          _createTextVNode(" " + _toDisplayString(selectedUser.value.email), 1)
                                        ])
                                      ])
                                    ]),
                                    _: 1
                                  }, 8, ["title"])
                                ]))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_el_divider),
            _createElementVNode("div", _hoisted_10, [
              _cache[25] || (_cache[25] = _createElementVNode("h3", { class: "section-title" }, "Message Content", -1)),
              _createVNode(_component_el_row, { gutter: 20 }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_col, { span: 8 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_form_item, {
                        label: "Title (AZ)",
                        required: ""
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_input, {
                            modelValue: form.titleAz,
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.titleAz) = $event)),
                            placeholder: "Enter title in Azerbaijani",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_col, { span: 8 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_form_item, {
                        label: "Title (EN)",
                        required: ""
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_input, {
                            modelValue: form.titleEn,
                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((form.titleEn) = $event)),
                            placeholder: "Enter title in English",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_col, { span: 8 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_form_item, {
                        label: "Title (RU)",
                        required: ""
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_input, {
                            modelValue: form.titleRu,
                            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.titleRu) = $event)),
                            placeholder: "Enter title in Russian",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_el_row, { gutter: 20 }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_col, { span: 8 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_form_item, {
                        label: "Message (AZ)",
                        required: ""
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_input, {
                            modelValue: form.messageTextAz,
                            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((form.messageTextAz) = $event)),
                            type: "textarea",
                            rows: 4,
                            placeholder: "Enter message text in Azerbaijani",
                            resize: "vertical",
                            "show-word-limit": "",
                            maxlength: "500"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_col, { span: 8 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_form_item, {
                        label: "Message (EN)",
                        required: ""
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_input, {
                            modelValue: form.messageTextEn,
                            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((form.messageTextEn) = $event)),
                            type: "textarea",
                            rows: 4,
                            placeholder: "Enter message text in English",
                            resize: "vertical",
                            "show-word-limit": "",
                            maxlength: "500"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_col, { span: 8 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_form_item, {
                        label: "Message (RU)",
                        required: ""
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_input, {
                            modelValue: form.messageTextRu,
                            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((form.messageTextRu) = $event)),
                            type: "textarea",
                            rows: 4,
                            placeholder: "Enter message text in Russian",
                            resize: "vertical",
                            "show-word-limit": "",
                            maxlength: "500"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_el_form_item, { label: "Image (Optional)" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_11, [
                    (!selectedImage.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                          _createVNode(_component_el_upload, {
                            class: "compact-upload",
                            "auto-upload": false,
                            "on-change": handleImageUpload,
                            "show-file-list": false,
                            accept: "image/*",
                            limit: 1
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_13, [
                                _createVNode(_component_el_icon, { class: "upload-icon" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(UploadFilled))
                                  ]),
                                  _: 1
                                }),
                                _cache[22] || (_cache[22] = _createElementVNode("span", { class: "upload-text" }, "Click to upload image", -1)),
                                _cache[23] || (_cache[23] = _createElementVNode("span", { class: "upload-hint" }, "JPG/PNG up to 2MB", -1))
                              ])
                            ]),
                            _: 1
                          })
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                          _createElementVNode("img", {
                            src: imagePreview.value,
                            alt: "Uploaded image",
                            class: "preview-image"
                          }, null, 8, _hoisted_15),
                          _createElementVNode("div", _hoisted_16, [
                            _createVNode(_component_el_button, {
                              onClick: clearImage,
                              type: "danger",
                              size: "small",
                              class: "delete-button"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_icon, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(Delete))
                                  ]),
                                  _: 1
                                }),
                                _cache[24] || (_cache[24] = _createTextVNode(" Remove "))
                              ]),
                              _: 1,
                              __: [24]
                            })
                          ])
                        ]))
                  ])
                ]),
                _: 1
              })
            ]),
            _createVNode(_component_el_form_item, { class: "submit-row" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_button, {
                  type: "primary",
                  onClick: sendNotification,
                  disabled: !canSendNotification.value,
                  loading: isSending.value,
                  class: "send-button"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_icon, null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Promotion))
                      ]),
                      _: 1
                    }),
                    _cache[26] || (_cache[26] = _createTextVNode(" Send "))
                  ]),
                  _: 1,
                  __: [26]
                }, 8, ["disabled", "loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"])
      ])
    ]),
    _: 1
  }))
}
}

})